import { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import {
  useCreateTasksAndWorkLogsForEmployeesMutation,
  useGetAutomaticWorkLogEmployeeOptionsQuery,
  useGetTaskPrioritiesQuery,
} from '../../api/gridApi';
import type { AutomaticWorkLogEmployeeOption, CreateTasksAndWorkLogsInput } from '../../types';
import { extractErrorMessage } from '../../../../utils/errorUtils';
import {
  formatLocalDateTime,
  getLocalDurationHours,
  validateAutomaticWorkLogInput,
} from './createTasksAndWorkLogsUtils';

type CreateTasksAndWorkLogsDialogProps = {
  open: boolean;
  title?: string | null;
  projectId: number;
  nodeKey: string;
  onClose: () => void;
  onSuccess: (message: string) => void;
};

export const CreateTasksAndWorkLogsDialog = ({
  open,
  title,
  projectId,
  nodeKey,
  onClose,
  onSuccess,
}: CreateTasksAndWorkLogsDialogProps) => {
  const [taskName, setTaskName] = useState('');
  const [priorityId, setPriorityId] = useState<number | null>(null);
  const [employees, setEmployees] = useState<AutomaticWorkLogEmployeeOption[]>([]);
  const [logStart, setLogStart] = useState('');
  const [logEnd, setLogEnd] = useState('');
  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const prioritiesQuery = useGetTaskPrioritiesQuery(undefined, { skip: !open });
  const employeesQuery = useGetAutomaticWorkLogEmployeeOptionsQuery(projectId, { skip: !open });
  const [createTasksAndWorkLogs, createState] = useCreateTasksAndWorkLogsForEmployeesMutation();
  const employeeOptions = employeesQuery.data ?? [];
  const priorities = prioritiesQuery.data ?? [];
  const loadError = prioritiesQuery.error
    ? extractErrorMessage(prioritiesQuery.error, 'Failed to load task priorities.')
    : employeesQuery.error
      ? extractErrorMessage(employeesQuery.error, 'Failed to load active employees.')
      : !employeesQuery.isLoading && employeesQuery.isSuccess && employeeOptions.length === 0
        ? 'No active employees found.'
        : null;
  const errorMessage =
    loadError ??
    (createState.error
      ? extractErrorMessage(createState.error, 'Failed to create tasks and work logs.')
      : validationMessage);

  const hours = useMemo(() => {
    if (!logStart || !logEnd) return '';
    const value = getLocalDurationHours(logStart, logEnd);
    return value === null ? '' : String(value);
  }, [logStart, logEnd]);

  const resetForm = () => {
    setTaskName('');
    setPriorityId(null);
    setEmployees([]);
    setLogStart('');
    setLogEnd('');
    setValidationMessage(null);
    createState.reset();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleCreate = async () => {
    const selectedEmployeeIds = employees.map((employee) => employee.crmRelationId);
    const validationError = validateAutomaticWorkLogInput(
      taskName,
      selectedEmployeeIds,
      logStart,
      logEnd
    );
    if (validationError) {
      setValidationMessage(validationError);
      return;
    }

    setValidationMessage(null);
    const input: CreateTasksAndWorkLogsInput = {
      projectId,
      nodeKey,
      taskName: taskName.trim(),
      priorityId,
      selectedEmployeeIds,
      logStart: formatLocalDateTime(logStart),
      logEnd: formatLocalDateTime(logEnd),
    };

    try {
      const result = await createTasksAndWorkLogs(input).unwrap();
      resetForm();
      onSuccess(`${result.createdWorkLogsCount} Work Logs created successfully.`);
    } catch {
      // The RTK error is displayed in the dialog.
    }
  };

  const isLoading = prioritiesQuery.isLoading || employeesQuery.isLoading || createState.isLoading;
  const isCreateDisabled = isLoading || !!loadError;

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown={createState.isLoading}
      onClose={(_event, reason) => {
        if (!createState.isLoading && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
          handleClose();
        }
      }}
    >
      <DialogTitle>{title || 'Create Tasks and Work Logs'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {(prioritiesQuery.isLoading || employeesQuery.isLoading) && (
            <Stack alignItems="center" py={2}>
              <CircularProgress size={24} />
            </Stack>
          )}
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          <TextField
            required
            label="Task Name"
            value={taskName}
            multiline
            minRows={3}
            disabled={isLoading}
            onChange={(event) => {
              setValidationMessage(null);
              setTaskName(event.target.value);
            }}
          />
          <Stack direction="row" justifyContent="flex-end">
            <Button
              size="small"
              disabled={isLoading || employeeOptions.length === 0}
              onClick={() => {
                setValidationMessage(null);
                setEmployees(employeeOptions);
              }}
            >
              Select All
            </Button>
          </Stack>
          <Autocomplete
            multiple
            options={employeeOptions}
            value={employees}
            disabled={isLoading}
            getOptionLabel={(employee) => employee.crmRelationName}
            isOptionEqualToValue={(option, value) => option.crmRelationId === value.crmRelationId}
            onChange={(_event, value) => {
              setValidationMessage(null);
              setEmployees(value);
            }}
            renderInput={(params) => (
              <TextField {...params} required label="Employees" placeholder="Select employees" />
            )}
          />
          <FormControl fullWidth disabled={isLoading}>
            <InputLabel id="automatic-work-log-priority-label">Priority</InputLabel>
            <Select
              labelId="automatic-work-log-priority-label"
              label="Priority"
              value={priorityId ?? ''}
              onChange={(event) => {
                setValidationMessage(null);
                setPriorityId(event.target.value ? Number(event.target.value) : null);
              }}
            >
              <MenuItem value="">None</MenuItem>
              {priorities.map((priority) => (
                <MenuItem key={priority.priorityId} value={priority.priorityId}>
                  {priority.priorityName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            required
            label="Work Log Start"
            type="datetime-local"
            value={logStart}
            InputLabelProps={{ shrink: true }}
            disabled={isLoading}
            onChange={(event) => {
              setValidationMessage(null);
              setLogStart(event.target.value);
            }}
          />
          <TextField
            required
            label="Work Log End"
            type="datetime-local"
            value={logEnd}
            InputLabelProps={{ shrink: true }}
            disabled={isLoading}
            onChange={(event) => {
              setValidationMessage(null);
              setLogEnd(event.target.value);
            }}
          />
          <TextField label="Hours" value={hours} InputProps={{ readOnly: true }} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={createState.isLoading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleCreate} disabled={isCreateDisabled}>
          {createState.isLoading ? <CircularProgress size={20} /> : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTasksAndWorkLogsDialog;
